/* Product page module. Finish + size selectors resolve a variant, update
   ?variant= without reload, and cross-fade the gallery photo (size selector is
   the deliberate departure — selecting a size swaps the photograph). Sticky ATC
   bar, lightbox, recently-viewed writer, upsell modal. Values via CSS tokens. */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const money = (cents) => {
  const amount = (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const code = window.Shopify?.currency?.active || 'USD';
  if (code === 'MXN') return `${amount} MXN`;
  if (code !== 'USD') return `$${amount} ${code}`;
  return (window.PLANTFORM?.moneyFormat || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, amount);
};

/* slower, eased scrollTop tween — native `behavior:'smooth'` is quick and
   near-linear; this reads more deliberate for the thumb-rail's bigger moves */
function smoothScrollTop(el, target, duration) {
  const max = el.scrollHeight - el.clientHeight;
  target = Math.max(0, Math.min(target, max));
  const start = el.scrollTop;
  const delta = target - start;
  if (!delta) return;
  cancelAnimationFrame(el._scrollRaf);
  const t0 = performance.now();
  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const step = (now) => {
    const p = Math.min(1, (now - t0) / duration);
    el.scrollTop = start + delta * easeInOutCubic(p);
    if (p < 1) el._scrollRaf = requestAnimationFrame(step);
  };
  el._scrollRaf = requestAnimationFrame(step);
}

/* ------------------------------------------------------------------ PDP core */
class PFProduct extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.dataset.variants || '[]');
    try { this.tradePrices = this.dataset.tradePrices ? JSON.parse(this.dataset.tradePrices) : null; }
    catch (_) { this.tradePrices = null; }
    this.form = this.querySelector('#product-form') || this.querySelector('[data-product-form]');
    this.idInput = this.querySelector('[data-variant-id]');
    this.gallery = this.querySelector('[data-gallery]');
    this.selectors = Array.from(this.querySelectorAll('[data-selector]'));
    this.current = this.variants.find((v) => String(v.id) === this.dataset.currentVariant) || this.variants[0];

    this.selectors.forEach((fs) => {
      fs.addEventListener('change', () => this._onSelect());
    });

    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!this.current?.available || this.querySelector('[data-add-to-cart][data-sold-out]')) return;
      const qty = Number(this.form.querySelector('[name="quantity"]').value) || 1;
      document.dispatchEvent(new CustomEvent('pf-cart:add', {
        detail: { id: this.current.id, quantity: qty, trigger: this.querySelector('[data-add-to-cart]') }
      }));
    });

    this._initDesc();
    this._initGallery();
    this._initSticky();
    this._initLightbox();
    this._initPacks();
    this._initShapes();
    this._applyStaticVariant();
  }

  /* static pack cards aren't real variants, so keep their is-selected class
     in sync with the checked radio (variant packs go through _syncSelectorClasses) */
  _initPacks() {
    this.packs = Array.from(this.querySelectorAll('[data-static-pack]'));
    if (!this.packs.length) return;

    const mark = () => {
      const sel = this.packs.find((p) => p.querySelector('input[type="radio"]')?.checked) || this.packs[0];
      this.packs.forEach((p) => p.classList.toggle('is-selected', p === sel));
    };
    this.packs.forEach((p) => p.querySelector('input[type="radio"]')?.addEventListener('change', () => { mark(); this._applyStaticVariant(); }));
    mark();
  }

  /* The finish/shape cards and set-size cards are theme-drawn, but checkout
     needs a real variant. Pick the variant whose options contain the chosen
     finish (or shape) AND the chosen set count; if the product doesn't have
     such variants yet, leave the current variant alone. */
  _applyStaticVariant() {
    if (!this.packs || !this.packs.length) return;
    const sel = this.packs.find((p) => p.querySelector('input[type="radio"]')?.checked) || this.packs[0];
    const count = sel && sel.dataset.packCount;
    if (!count) return;
    const finishEl = this.querySelector('[data-selector="finish"] input:checked');
    const shapeEl = (this.shapes || []).find((s) => s.querySelector('input[type="radio"]')?.checked) || (this.shapes || [])[0];
    const finish = finishEl ? finishEl.value : (shapeEl ? shapeEl.dataset.value : null);
    const countRe = new RegExp('(^|\\D)' + count + '(\\D|$)');
    const match = this.variants.find((v) => v.options.some((o) => countRe.test(o)) && (finish == null || v.options.includes(finish)));
    if (!match || match === this.current) return;
    this.current = match;
    this.idInput.value = match.id;
    const url = new URL(window.location);
    url.searchParams.set('variant', match.id);
    history.replaceState({}, '', url);
    this._updatePrice();
    this._updateMeta();
  }

  /* same idea as _initPacks — static shape swatches (Discs / Rings) aren't
     tied to real variant options, so nothing was keeping .is-selected in
     sync with the checked radio: clicking "Rings" checked the input but the
     highlight never moved off "Discs", reading as if the click did nothing */
  _initShapes() {
    this.shapes = Array.from(this.querySelectorAll('[data-static-shape]'));
    if (!this.shapes.length) return;
    this.packUnits = Array.from(this.querySelectorAll('[data-pack-unit]'));
    this.packPrices = Array.from(this.querySelectorAll('[data-price-map]'));

    const mark = () => {
      const sel = this.shapes.find((s) => s.querySelector('input[type="radio"]')?.checked) || this.shapes[0];
      this.shapes.forEach((s) => s.classList.toggle('is-selected', s === sel));
      const unit = sel?.dataset.value;
      // pack cards ("20 Discs", "40 Discs"...) reflect whichever shape is
      // selected instead of always saying the default shape's unit word —
      // but only for shape blocks opted into this (e.g. Discs/Rings), not
      // a finish selector (e.g. Mirror Polished/Classic Brushed), which
      // would otherwise overwrite "Pins" with the finish name.
      if (unit && sel.hasAttribute('data-sync-pack-unit')) {
        const word = sel.dataset.packUnitWord || unit;   // e.g. "Thorn Pins" instead of just "Thorn"
        this.packUnits.forEach((u) => { u.textContent = word; });
      }
      // some products price each pack differently per finish (e.g. Tree
      // Pins: Mirror Polished vs Classic Brushed) — swap the displayed
      // price to match whichever finish is selected.
      if (unit) {
        this.packPrices.forEach((el) => {
          let map;
          try { map = JSON.parse(el.dataset.priceMap); } catch (_) { return; }
          if (map && map[unit] != null) el.textContent = map[unit];
        });
        // same idea for the struck-through "was" price and the "Save $x" line
        [['[data-was-map]', 'wasMap'], ['[data-save-map]', 'saveMap']].forEach(([sel2, key]) => {
          this.querySelectorAll(sel2).forEach((el) => {
            let map;
            try { map = JSON.parse(el.dataset[key]); } catch (_) { return; }
            if (map && map[unit] != null) el.textContent = map[unit];
          });
        });
      }
    };
    this.shapes.forEach((s) => s.querySelector('input[type="radio"]')?.addEventListener('change', () => { mark(); this._applyStaticVariant(); }));
    mark();
    this._applyStaticVariant();
  }

  _updateFinishName() {
    const el = this.querySelector('[data-finish-name]');
    if (!el) return;
    const f = this.querySelector('[data-selector="finish"] input:checked');
    if (f) el.textContent = f.value;
  }

  _selectedOptions() {
    // option position -> value, from checked radios in each selector
    const opts = [];
    this.selectors.forEach((fs) => {
      const pos = Number(fs.dataset.optionPosition);
      const checked = fs.querySelector('input:checked');
      if (checked) opts[pos - 1] = checked.value;
    });
    return opts;
  }

  _onSelect() {
    const want = this._selectedOptions();
    const match = this.variants.find((v) =>
      v.options.every((opt, i) => want[i] == null || want[i] === opt));
    if (!match) { this._setUnavailable(want); return; }

    this.current = match;
    this.idInput.value = match.id;

    // URL ?variant= without reload
    const url = new URL(window.location);
    url.searchParams.set('variant', match.id);
    history.replaceState({}, '', url);

    this._syncSelectorClasses();
    this._updateFinishName();
    this._updatePrice();
    this._updateMeta();
    this._swapGallery(match);
    this._applyStaticVariant();
  }

  _syncSelectorClasses() {
    this.querySelectorAll('[data-swatch], [data-size]').forEach((el) => {
      el.classList.toggle('is-selected', el.querySelector('input')?.checked);
    });
  }

  _updatePrice() {
    const trade = this.tradePrices && this.tradePrices[this.current.id];
    const shown = trade != null ? trade : this.current.price;

    const el = this.querySelector('[data-price-amount]');
    if (el) el.textContent = money(shown);

    const was = this.querySelector('[data-price-was]');
    if (was) {
      // trade: strike the list price; otherwise strike compare-at when present
      const strike = trade != null ? this.current.price
        : (this.current.compare_at_price > this.current.price ? this.current.compare_at_price : null);
      was.hidden = strike == null;
      if (strike != null) was.textContent = money(strike);
    }
    const sticky = this.querySelector('[data-sticky-price]');
    if (sticky) sticky.textContent = money(shown);
  }

  _updateMeta() {
    const sku = this.querySelector('[data-sku]');
    if (sku && this.current.sku) sku.textContent = `${window.PLANTFORM.strings.sku || 'SKU'} ${this.current.sku}`;

    const btn = this.querySelector('[data-add-to-cart]');
    const label = this.querySelector('[data-add-label]');
    if (btn.hasAttribute('data-sold-out')) {
      btn.disabled = true;
      label.textContent = window.PLANTFORM.strings.soldOut;
    } else if (this.current.available) {
      btn.disabled = false;
      label.textContent = window.PLANTFORM.strings.addToCart;
    } else {
      btn.disabled = true;
      label.textContent = window.PLANTFORM.strings.soldOut;
    }
  }

  _setUnavailable(want) {
    const btn = this.querySelector('[data-add-to-cart]');
    const label = this.querySelector('[data-add-label]');
    btn.disabled = true;
    label.textContent = window.PLANTFORM.strings.soldOut;
    this._syncSelectorClasses();
  }

  /* ------------------------------------------------------------ gallery swap */
  _initGallery() {
    if (!this.gallery) return;
    this.slides = Array.from(this.gallery.querySelectorAll('.pdp-gallery__slide'));
    this.thumbs = Array.from(this.gallery.querySelectorAll('[data-gallery-thumb]'));
    this.dots = Array.from(this.gallery.querySelectorAll('[data-dot]'));
    this._gi = 0;
    this.slides.forEach((sl) => sl.querySelector('.pdp-gallery__image')?.setAttribute('loading', 'eager'));
    const stage = this.gallery.querySelector('[data-gallery-stage]');
    const mobile = window.matchMedia('(max-width: 989px)');
    const rail = this.gallery.querySelector('[data-gallery-rail]');

    // reflect index i in the thumb rail + dots (and centre the active thumb, B+P style)
    const mark = (i) => {
      this._gi = i;
      this.thumbs.forEach((t, ti) => {
        const on = ti === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      // centre the active thumb by scrolling ONLY the rail — never scrollIntoView,
      // which also scrolls the page/document and makes it jump on mobile.
      // Mobile rail scrolls horizontally; desktop's is a vertical column
      // (only 4 squares' worth is visible, the rest are off the bottom).
      if (rail && this.thumbs[i]) {
        const t = this.thumbs[i];
        if (mobile.matches) {
          const left = t.offsetLeft - (rail.clientWidth - t.clientWidth) / 2;
          rail.scrollTo({ left, behavior: REDUCED.matches ? 'auto' : 'smooth' });
        } else {
          const top = t.offsetTop - (rail.clientHeight - t.clientHeight) / 2;
          // native `smooth` is quick/linear-ish; a slower eased tween reads
          // more deliberate for this bigger vertical move
          if (REDUCED.matches) rail.scrollTop = top;
          else smoothScrollTop(rail, top, 700);
        }
      }
      this.dots.forEach((d, di) => d.classList.toggle('is-active', di === i));
    };

    // wrap-around on mobile: park the incoming slide next to the current one
    // (CSS order + instant jump), then slide one step like any other move
    const wrap = (dir) => {
      const n = this.slides.length;
      const dur = REDUCED.matches ? 0 : 420;
      const step = stage.clientWidth + 1;
      const incoming = this.slides[dir < 0 ? n - 1 : 0];
      const target = dir < 0 ? n - 1 : 0;
      this._wrapping = true;
      stage.style.scrollSnapType = 'none';
      incoming.style.order = dir < 0 ? '-1' : '99';
      stage.scrollLeft = dir < 0 ? step : step * (n - 2);
      stage.scrollTo({ left: dir < 0 ? 0 : step * (n - 1), behavior: dur ? 'smooth' : 'auto' });
      mark(target);
      setTimeout(() => {
        incoming.style.order = '';
        stage.scrollLeft = target * step;
        stage.style.scrollSnapType = '';
        this._wrapping = false;
      }, dur + 60);
    };

    // mobile arrows: fade old -> new (a copy of the old photo fades out over the new one)
    const fadeTo = (i) => {
      const n = this.slides.length;
      i = ((i % n) + n) % n;
      if (i === this._gi) return;
      const frame = stage.parentElement;
      const curImg = this.slides[this._gi]?.querySelector('.pdp-gallery__image');
      const nextImg = this.slides[i]?.querySelector('.pdp-gallery__image');
      const left = this.slides[i].offsetLeft - this.slides[0].offsetLeft;
      const jump = () => {
        stage.style.scrollSnapType = 'none';
        stage.scrollTo({ left, behavior: 'instant' });
        stage.style.scrollSnapType = '';
        mark(i);
      };
      if (REDUCED.matches || !curImg || !frame) { jump(); return; }
      const run = () => {
        const veil = document.createElement('img');
        veil.className = 'pdp-gallery__veil';
        veil.alt = '';
        veil.src = curImg.currentSrc || curImg.src;
        frame.appendChild(veil);
        jump();
        requestAnimationFrame(() => requestAnimationFrame(() => { veil.style.opacity = '0'; }));
        setTimeout(() => veil.remove(), 500);
      };
      if (nextImg && nextImg.complete) run();
      else (nextImg && nextImg.decode ? nextImg.decode() : Promise.resolve()).catch(() => {}).then(run);
    };

    const go = (i, fade) => {
      if (!this.slides.length) return;
      if (fade && mobile.matches && stage) { fadeTo(i); return; }
      if (mobile.matches && stage && !this._wrapping && this.slides.length > 1 && (i < 0 || i >= this.slides.length)) {
        wrap(i < 0 ? -1 : 1);
        return;
      }
      i = ((i % this.slides.length) + this.slides.length) % this.slides.length;
      if (mobile.matches && stage) {
        // native scroll-snap carousel — physically slide, no hard cut.
        // use the slide's real offset so the inter-slide white gap is accounted for
        const target = this.slides[i];
        const left = target ? target.offsetLeft - this.slides[0].offsetLeft : i * stage.clientWidth;
        stage.scrollTo({ left, behavior: REDUCED.matches ? 'auto' : 'smooth' });
        mark(i);
        return;
      }
      const leaving = this.slides[this._gi];
      if (leaving && leaving !== this.slides[i] && !REDUCED.matches) {
        leaving.classList.add('is-leaving');
        clearTimeout(this._leaveT);
        this._leaveT = setTimeout(() => this.slides.forEach((s) => s.classList.remove('is-leaving')), 600);
      }
      this.slides.forEach((s, si) => { s.hidden = false; s.classList.toggle('is-active', si === i); });
      mark(i);
    };
    this._galleryGo = go;

    // desktop crossfade needs every slide rendered (the global [hidden]{display:none}
    // would kill the opacity transition), so drop the attribute outside mobile
    const unhideForDesktop = () => { if (!mobile.matches) this.slides.forEach((s) => { s.hidden = false; }); };
    unhideForDesktop();
    mobile.addEventListener('change', unhideForDesktop);

    this.thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.galleryThumb))));
    this.dots.forEach((d) => d.addEventListener('click', () => go(Number(d.dataset.dot))));
    this.gallery.querySelectorAll('[data-gallery-prev]').forEach((b) => b.addEventListener('click', () => go(this._gi - 1, true)));
    this.gallery.querySelectorAll('[data-gallery-next]').forEach((b) => b.addEventListener('click', () => go(this._gi + 1, true)));

    // mobile: derive the active index from the stage's scroll position
    if (stage) {
      let raf = 0;
      stage.addEventListener('scroll', () => {
        if (!mobile.matches || raf || this._wrapping) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const step = stage.clientWidth + 1;   // slide width + white gap
          const i = Math.round(stage.scrollLeft / step);
          if (i !== this._gi && i >= 0 && i < this.slides.length) mark(i);
        });
      }, { passive: true });
    }
  }

  _swapGallery(variant) {
    // static brand gallery (not tied to product media) — never swap the hero on select
    if (this.gallery && this.gallery.hasAttribute('data-gallery-static')) return;
    if (!variant.featured_media || !this.slides) return;
    const idx = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(variant.featured_media.id));
    if (idx > -1) { this._galleryGo?.(idx); return; }
    // no dedicated media for this variant — cross-fade the current stage image
    const primary = this.slides[this._gi || 0];
    const img = primary && primary.querySelector('.pdp-gallery__image');
    const newSrc = variant.featured_media.preview_image?.src || variant.featured_media.src;
    if (!img || !newSrc || img.src === newSrc) return;

    const apply = () => {
      img.src = newSrc;
      img.srcset = '';
      primary.classList.remove('is-swapping');
    };
    if (REDUCED.matches) { apply(); return; }
    primary.classList.add('is-swapping');
    img.addEventListener('transitionend', function onEnd() {
      img.removeEventListener('transitionend', onEnd);
      apply();
    }, { once: true });
    // safety timeout in case transitionend doesn't fire
    setTimeout(apply, 300);
  }

  /* ------------------------------------------------------------- sticky ATC */
  _initSticky() {
    const bar = this.querySelector('[data-sticky-atc]');
    const anchor = this.querySelector('[data-add-to-cart]');
    if (!bar || !anchor) return;
    bar.hidden = false;
    const io = new IntersectionObserver(([entry]) => {
      // only once the page has actually scrolled a fair bit — so expanding
      // "Read more" near the top doesn't pop the bar in
      const show = !entry.isIntersecting && window.scrollY > 400;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }, { rootMargin: '-120px 0px 0px 0px' });
    io.observe(anchor);
    // re-evaluate on scroll so it can appear/disappear past the threshold
    let raf = 0;
    window.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const r = anchor.getBoundingClientRect();
        const show = (r.bottom < 120 || r.top > innerHeight) && window.scrollY > 400;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      });
    }, { passive: true });
    bar.querySelector('[data-sticky-add]')?.addEventListener('click', () => anchor.click());
  }

  /* --------------------------------------------------------------- lightbox */
  _initLightbox() {
    const dialog = this.querySelector('[data-lightbox]');
    const stage = this.querySelector('[data-lightbox-stage]');
    if (!dialog) return;
    this.querySelectorAll('[data-zoom]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const fig = btn.closest('.pdp-gallery__slide');
        const srcImg = fig.querySelector('img');
        if (srcImg) {
          const alt = srcImg.getAttribute('alt') || '';
          stage.innerHTML = '';
          const big = document.createElement('img');
          big.src = srcImg.currentSrc || srcImg.src;
          big.alt = alt;
          stage.appendChild(big);
        }
        dialog.showModal();
      });
    });
    dialog.querySelector('[data-lightbox-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
  }

  /* --------------------------------------------------- description read-more */
  _initDesc() {
    const wrap = this.querySelector('[data-desc]');
    const clip = this.querySelector('[data-desc-clip]');
    const btn = this.querySelector('[data-desc-toggle]');
    if (!wrap || !clip || !btn) return;
    // only show the toggle if the text is actually clipped
    if (clip.scrollHeight <= clip.clientHeight + 2) { btn.hidden = true; return; }

    btn.addEventListener('click', () => {
      const open = wrap.classList.toggle('is-open');
      btn.textContent = open ? 'Read less' : 'Read more';

      if (REDUCED.matches) { clip.style.maxHeight = open ? 'none' : ''; return; }

      // animate max-height between the collapsed clamp and the real content height
      clip.style.maxHeight = clip.scrollHeight + 'px';
      if (open) {
        clip.addEventListener('transitionend', function done(e) {
          if (e.propertyName !== 'max-height') return;
          clip.removeEventListener('transitionend', done);
          clip.style.maxHeight = 'none';
        });
      } else {
        // from the explicit current height, on the next frame go back to the clamp
        requestAnimationFrame(() => { clip.style.maxHeight = ''; });
      }
    });
  }
}
customElements.define('pf-product', PFProduct);
customElements.define('pf-gallery', class extends HTMLElement {});

/* ------------------------------------------------------------- upsell modal */
const upsell = document.querySelector('[data-upsell]');
if (upsell) {
  document.addEventListener('pf-cart:added', () => {
    if (typeof upsell.showModal === 'function') upsell.showModal();
  });
  upsell.querySelectorAll('[data-upsell-dismiss]').forEach((b) => b.addEventListener('click', () => upsell.close()));
  upsell.addEventListener('click', (e) => { if (e.target === upsell) upsell.close(); });
  upsell.querySelectorAll('[data-upsell-add]').forEach((b) => {
    b.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('pf-cart:add', { detail: { id: Number(b.dataset.upsellAdd), quantity: 1, trigger: b } }));
    });
  });
}
